import { lazy, Suspense } from 'react';
import { useRouter } from '@tanstack/react-router';
import { T } from 'gt-react';
import ErrorBoundary from '@/components/ErrorBoundary';
import { NamiPreviewCard } from '../nami/NamiPreviewCard';
import { TableExportActions } from '../TableExportActions';
import { showViewFrom } from './showViewState';
import { useViewCommands } from './useViewCommands';

// The Apps component set is only needed once a view is shown.
const AppView = lazy(() =>
  import('@erp/json-renderer/apps/react').then((module) => ({
    default: module.AppView,
  })),
);

interface ShowViewToolResultProps {
  /** The `show_view` tool-call args: `{ title?, manifest }`. */
  args: Record<string, unknown>;
  /** Its result: only a call that validated (`{ ok: true }`) is shown. */
  result: unknown;
}

/**
 * Inline generative UI over the Apps layer: renders the view of a `show_view`
 * manifest with its snapshot feeds in state. A button bound to a command
 * previews its action as the user; the preview card under the view applies it.
 */
export function ShowViewToolResult({ args, result }: ShowViewToolResultProps) {
  const router = useRouter();
  const shown = showViewFrom(args, result);
  const { handlers, previews } = useViewCommands(shown?.commands ?? {});
  if (!shown) return null;
  return (
    <figure className="min-w-0 space-y-2 [contain:inline-size] [&_.overflow-x-auto.border]:max-h-[80vh] [&_.overflow-x-auto.border]:overflow-auto [&_.overflow-x-auto.border_thead]:sticky [&_.overflow-x-auto.border_thead]:top-0 [&_.overflow-x-auto.border_thead]:bg-background [&_.overflow-x-auto.border_thead]:shadow-xs">
      {shown.title && (
        <figcaption className="text-sm font-medium text-foreground">
          {shown.title}
        </figcaption>
      )}
      <ErrorBoundary
        fallback={
          <p className="text-sm text-muted-foreground">
            <T context="chat: an inline view from the assistant failed to render">
              This view couldn&apos;t be shown.
            </T>
          </p>
        }
      >
        <Suspense
          fallback={<div className="h-24 animate-pulse rounded-md bg-muted" />}
        >
          <div className="overflow-x-auto">
            <AppView
              spec={shown.view}
              state={shown.state}
              handlers={handlers}
              navigate={(to) => router.history.push(to)}
              renderTableActions={(columns, rows) => (
                <TableExportActions
                  columns={columns}
                  rows={rows}
                  title={shown.title}
                />
              )}
            />
          </div>
        </Suspense>
      </ErrorBoundary>
      {previews.map((preview) => (
        <NamiPreviewCard key={preview.pendingActionId} result={preview} />
      ))}
    </figure>
  );
}
